/* eslint-disable react/prop-types */
import React from "react";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";
import { nFormatter } from "lib/utils";
import { SellOut } from "resources/constants";
import getReducedHistorics from "lib/utils/historics";
import CardComponent from "app/common/CardComponent";
import AggregateDoughnutComponent from "./AggregateDoughnutComponent";

const useStyles = makeStyles((theme) => ({
  cardBody: {
    height: "calc(100% - 24px)",
    overflow: "hidden",
  },
  cardRow: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    height: "33%",
    overflow: "hidden",
  },
  cardRowItem: {
    flex: "50%",
    overflow: "hidden",
  },
  secondColumn: {
    paddingLeft: "10px",
  },
  chartContainer: {
    position: "relative",
    [theme.breakpoints.up("xs")]: {
      overflow: "hidden",
      flexGrow: "1",
      height: "105px",
    },
    [theme.breakpoints.up("lg")]: {
      overflow: "hidden",
      flexGrow: "1",
      height: "100%",
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
}));

function HistoryAggregateComponent({ historics }) {
  const classes = useStyles();
  const historicsSlice = useSelector((state) => state.historics);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? `${SellOut} in scope`
    : "Sell out in scope";
  const sellOutLabelByTenantNotScope = configCatSlice?.features.isUsaTerminology
    ? `${SellOut} not in scope`
    : "Sell out not in scope";

  const reducedTotalHistorics = getReducedHistorics(
    historicsSlice.tuples[
      `undefined-undefined-undefined-undefined-undefined-undefined-undefined`
    ].historics
  );
  const reducedFilteredHistorics = getReducedHistorics(historics);

  const sellOutData = {
    labels: [sellOutLabelByTenant, sellOutLabelByTenantNotScope],
    text: `${parseFloat(
      (
        (100 * reducedFilteredHistorics.units) /
        reducedTotalHistorics.units
      ).toFixed(1)
    )}%`,
    datasets: [
      {
        data: [
          reducedFilteredHistorics.units,
          reducedTotalHistorics.units - reducedFilteredHistorics.units,
        ],
        backgroundColor: ["#47B5DB", "#eaeaea"],
      },
    ],
  };

  const marginData = {
    labels: ["Margin in scope", "Margin not in scope"],
    text: `${parseFloat(
      (
        (100 * reducedFilteredHistorics.margin) /
        reducedTotalHistorics.margin
      ).toFixed(1)
    )}%`,
    datasets: [
      {
        data: [
          reducedFilteredHistorics.margin,
          reducedTotalHistorics.margin - reducedFilteredHistorics.margin,
        ],
        backgroundColor: ["#66AE91", "#eaeaea"],
      },
    ],
  };

  const promotionsData = {
    labels: ["Followed Changes", "Ignored Changes"],
    text:
      reducedFilteredHistorics.promotions === null
        ? "N/A"
        : `${parseFloat(
            (
              (100 * reducedFilteredHistorics.followed_promotions) /
              reducedFilteredHistorics.promotions
            ).toFixed(1)
          )}%`,
    datasets: [
      {
        data: [
          reducedFilteredHistorics.followed_promotions,
          reducedFilteredHistorics.promotions -
            reducedFilteredHistorics.followed_promotions,
        ],
        backgroundColor: ["#FBB03B", "#eaeaea"],
      },
    ],
  };

  return (
    <CardComponent
      title={
        <Typography variant="body1" className={classes.componentTitle}>
          Annual aggregate
        </Typography>
      }
      body={
        <div className={classes.cardBody}>
          <Grid
            item
            container
            xs={12}
            sm={12}
            md={12}
            lg={12}
            className={classes.cardRow}
          >
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.chartContainer}
            >
              <AggregateDoughnutComponent data={sellOutData} />
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.secondColumn}
            >
              <Typography variant="body2">
                <b>Volume</b>
              </Typography>
              <Typography variant="caption">
                {nFormatter(reducedFilteredHistorics.units)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            item
            container
            xs={12}
            sm={12}
            md={12}
            lg={12}
            className={classes.cardRow}
          >
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.chartContainer}
            >
              <AggregateDoughnutComponent data={marginData} />
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.secondColumn}
            >
              <Typography variant="body2">
                <b>Margin</b>
              </Typography>
              <Typography variant="caption">
                {nFormatter(reducedFilteredHistorics.margin)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            item
            container
            xs={12}
            sm={12}
            md={12}
            lg={12}
            className={classes.cardRow}
          >
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.chartContainer}
            >
              <AggregateDoughnutComponent
                data={promotionsData}
                scope="Promotions"
              />
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              md={6}
              lg={6}
              className={classes.secondColumn}
            >
              <Typography variant="body2">
                <b>Followed Price Changes</b>
              </Typography>
              <Typography variant="caption">
                {reducedFilteredHistorics.followed_promotions}
              </Typography>
            </Grid>
          </Grid>
        </div>
      }
    />
  );
}

export default HistoryAggregateComponent;
